import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { Search, Filter, List, CheckCircle2, Ban, Trash2, Info, X } from 'lucide-react';

type BookingRow = { id: number; status: string; created_at: string; screening_id?: number; screenings?: { start_time?: string; movies?: { title?: string } } };
type BookingDetails = {
  id: number;
  user_id: string;
  screening_id: number;
  seats: number;
  total_amount: number;
  status: string;
  payment_intent_id?: string | null;
  payment_status?: string | null;
  phonebooking?: boolean | null;
  phonenumber?: string | null;
  customer_name?: string | null;
  created_at: string;
  expires_at?: string | null;
  profiles?: { email: string };
  screenings?: {
    start_time: string;
    location: string;
    movies?: { title: string; organizer_id?: number };
  };
  booking_seats?: { seat_ids: string[] }[];
  qr_code?: string;
};
type EventOption = { id: number; label: string };

export default function AdminManageBookings() {
  const [q, setQ] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [status, setStatus] = useState<'all' | 'confirmed' | 'cancelled' | 'pending'>('all');
  const [eventId, setEventId] = useState<number | ''>('');

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [bookingResults, setBookingResults] = useState<BookingRow[]>([]);
  const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState<BookingDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const searchBookings = async () => {
    try {
      let qb = supabase
        .from('bookings')
        .select('*, screenings(*, movies(*))', { count: 'exact' })

      if (q) {
        const { data: profiles } = await supabase.from('profiles').select('id').ilike('email', `%${q}%`).limit(50)
        const ids = ((profiles || []) as { id: string }[]).map(p => p.id)
        if (!ids.length) {
          setBookingResults([])
          return
        }
        qb = qb.in('user_id', ids)
      }

      if (status !== 'all') qb = qb.eq('status', status)
      if (eventId) qb = qb.eq('screening_id', eventId)
      if (bookingId) qb = qb.eq('id', Number(bookingId))

      qb = qb.order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1)

      const { data } = await qb
      setBookingResults(data || [])
    } catch {
      toast.error('Failed to search bookings')
    }
  }

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('screenings')
        .select('id, start_time, movies(title)')
        .order('start_time', { ascending: true })
        .limit(200);
      const opts = ((data || []) as any[]).map(s => ({ id: s.id, label: `${s.movies?.title || '—'} · ${new Date(s.start_time).toLocaleString()}` }));
      setEventOptions(opts);
    })();
  }, []);

  useEffect(() => { searchBookings(); }, [status, eventId, bookingId, q, page]);

  const cancelBookingAdmin = async (row: BookingRow) => {
    try {
      const bookingId = row.id as number;
      const screeningId = row.screening_id as number;
      const { data: tickets } = await supabase
        .from('booking_ticket_types')
        .select('ticket_type, quantity')
        .eq('booking_id', bookingId);
      const ticketRows = (tickets || []) as { ticket_type: 'silver' | 'gold' | 'diamond'; quantity: number }[];
      for (const t of ticketRows) {
        const seatColumn = t.ticket_type === 'silver' ? 'silver_booked' : t.ticket_type === 'gold' ? 'gold_booked' : 'diamond_booked';
        const { data: seatData } = await supabase
          .from('seatssplitting')
          .select(seatColumn)
          .eq('screening_id', screeningId)
          .single();
        const current = ((seatData as Record<string, number>)?.[seatColumn] || 0);
        const updated = Math.max(0, current - t.quantity);
        const { error: updErr } = await supabase
          .from('seatssplitting')
          .update({ [seatColumn]: updated })
          .eq('screening_id', screeningId);
        if (updErr) {
          toast.error('Failed to update seat allocation');
          return;
        }
      }
      const { error: cancelErr } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', bookingId);
      if (cancelErr) {
        toast.error('Failed to cancel booking');
        return;
      }
      await supabase
        .from('booking_seats')
        .delete()
        .eq('booking_id', bookingId);
      await supabase
        .from('booking_qr_codes')
        .update({ expires_at: new Date().toISOString(), redeemed: true })
        .eq('booking_id', bookingId);
      setBookingResults(prev => prev.map(b => (b.id === bookingId ? { ...b, status: 'cancelled' } : b)));
      toast.success('Booking cancelled');
    } catch {
      toast.error('Failed to cancel booking');
    }
  };

  const viewBookingDetails = async (bookingId: number) => {
    setLoadingDetails(true);
    setShowDetailsModal(true);
    try {
      // Fetch booking base data
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (bookingError) throw bookingError;

      // Fetch user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', booking.user_id)
        .single();

      // Fetch screening details
      const { data: screening } = await supabase
        .from('screenings')
        .select('start_time, location, movie_id')
        .eq('id', booking.screening_id)
        .single();

      // Fetch movie details if screening exists
      let movie = null;
      if (screening) {
        const { data: movieData } = await supabase
          .from('movies')
          .select('title')
          .eq('id', screening.movie_id)
          .single();
        movie = movieData;
      }

      // Fetch seat IDs
      const { data: seats } = await supabase
        .from('booking_seats')
        .select('seat_ids')
        .eq('booking_id', bookingId);

      // Fetch QR code
      const { data: qrData } = await supabase
        .from('booking_qr_codes')
        .select('iv_base64, cipher_base64')
        .eq('booking_id', bookingId)
        .maybeSingle();

      // Combine all data
      const combinedData: BookingDetails = {
        ...booking,
        profiles: profile ? { email: profile.email } : undefined,
        screenings: screening ? {
          start_time: screening.start_time,
          location: screening.location,
          movies: movie ? { title: movie.title } : undefined
        } : undefined,
        booking_seats: seats || [],
        qr_code: qrData ? `v1:${qrData.iv_base64}:${qrData.cipher_base64}` : undefined
      };

      setSelectedBookingDetails(combinedData);
    } catch (err: any) {
      console.error('Failed to load booking details:', err);
      toast.error(`Failed to load booking details: ${err.message || 'Unknown error'}`);
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <>
      <div id="manage-bookings" className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">Manage Bookings</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <div className="flex items-center gap-2 col-span-2">
            <Search className="text-gray-500 dark:text-gray-300 h-4 w-4" />
            <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search by user email" className="flex-1 rounded-lg text-gray-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-indigo-600/50 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 placeholder:text-gray-500 dark:placeholder:text-gray-400 px-4" />
          </div>
          <div className="flex items-center gap-2">
            <List className="text-gray-500 dark:text-gray-300 h-4 w-4" />
            <select value={eventId} onChange={e => { const v = e.target.value ? Number(e.target.value) : ''; setEventId(v); setPage(1); }} className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3">
              <option value="">All events</option>
              {eventOptions.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-500 dark:text-gray-300 h-4 w-4" />
            <select value={status} onChange={e => setStatus(e.target.value as any)} className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3">
              <option value="all">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <input value={bookingId} onChange={e => { setBookingId(e.target.value); setPage(1); }} placeholder="Booking ID" className="rounded-lg text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 h-10 px-3" />
        </div>
        <div className="mt-4 space-y-3">
          {bookingResults.map(b => (
            <div key={b.id} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-gray-900 dark:text-white font-semibold flex items-center gap-2">#{b.id} · {b.screenings?.movies?.title || '—'} {b.status === 'confirmed' ? <CheckCircle2 className="text-green-500 h-4 w-4" /> : b.status === 'cancelled' ? <Ban className="text-red-500 h-4 w-4" /> : null}</p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{new Date(b.created_at).toLocaleString()} · {b.status}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => viewBookingDetails(b.id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                    title="View Details"
                  >
                    <Info className="h-4 w-4" />
                    Details
                  </button>
                  <button
                    onClick={() => cancelBookingAdmin(b)}
                    disabled={b.status !== 'confirmed'}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Cancel Booking"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded">Prev</button>
          <button onClick={() => setPage(p => p + 1)} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white rounded">Next</button>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailsModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Booking Details</h3>
              <button onClick={() => setShowDetailsModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              {loadingDetails ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Loading...</p>
              ) : selectedBookingDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Booking ID</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">#{selectedBookingDetails.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white capitalize">{selectedBookingDetails.status}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Customer Email</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.profiles?.email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Customer Name</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.customer_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Movie</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.screenings?.movies?.title || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Screening Time</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.screenings?.start_time ? new Date(selectedBookingDetails.screenings.start_time).toLocaleString() : '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.screenings?.location || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Number of Seats</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.seats}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Amount</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">€{selectedBookingDetails.total_amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Booking Type</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.phonebooking ? 'Phone Booking' : 'Online'}</p>
                    </div>
                    {selectedBookingDetails.phonebooking && selectedBookingDetails.phonenumber && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone Number</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white">{selectedBookingDetails.phonenumber}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Created At</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{new Date(selectedBookingDetails.created_at).toLocaleString()}</p>
                    </div>
                    {selectedBookingDetails.payment_intent_id && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Payment Intent ID</p>
                        <p className="text-base font-mono text-gray-900 dark:text-white break-all">{selectedBookingDetails.payment_intent_id}</p>
                      </div>
                    )}
                    {selectedBookingDetails.payment_status && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Payment Status</p>
                        <p className="text-base font-medium text-gray-900 dark:text-white capitalize">{selectedBookingDetails.payment_status}</p>
                      </div>
                    )}
                    {selectedBookingDetails.booking_seats && selectedBookingDetails.booking_seats.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Seat IDs</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedBookingDetails.booking_seats.flatMap(bs => bs.seat_ids).map((seatId, idx) => (
                            <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 text-sm font-mono">
                              {seatId}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedBookingDetails.qr_code && (
                      <div className="col-span-2 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg mt-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Booking QR Code</p>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                          <QRCodeSVG value={selectedBookingDetails.qr_code} size={150} level="H" includeMargin={true} />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 font-mono">{selectedBookingDetails.qr_code}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No details available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
